import { useState, type FormEvent } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { passwordIssues, type LoginResponse } from '@concours/shared'
import {
  Alert,
  AuthCard,
  Button,
  FullPageSpinner,
  PasswordInput,
  useAuthSession,
} from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'

/** Activation d'un compte interne invité : choix du mot de passe, puis session ouverte. */
export function InvitationPage() {
  const { t } = useTranslation()
  const session = useAuthSession()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')

  const preview = useQuery({
    queryKey: ['invitation', token],
    queryFn: () =>
      session.api.get<{ email: string; firstName: string | null }>('/auth/admin/invitation', {
        query: { token },
      }),
    enabled: token.length > 0,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () =>
      session.api.post<LoginResponse>('/auth/admin/invitation/accept', {
        token,
        password,
        passwordConfirmation: confirmation,
      }),
    onSuccess: (response) => {
      session.acceptSession(response)
      navigate('/compte', { replace: true })
    },
  })

  if (token && preview.isPending) return <FullPageSpinner />
  if (!token || preview.isError) {
    return (
      <AuthCard title={t('auth.invitation.title')}>
        <Alert tone="warning">{t('auth.invitation.invalid')}</Alert>
      </AuthCard>
    )
  }

  const email = preview.data!.email
  const mismatch = confirmation.length > 0 && confirmation !== password
  const ok = passwordIssues(password, email).length === 0 && !mismatch && confirmation.length > 0

  function submit(event: FormEvent) {
    event.preventDefault()
    if (ok) accept.mutate()
  }

  return (
    <AuthCard
      title={t('auth.invitation.title')}
      description={t('auth.invitation.description', { name: preview.data!.firstName ?? '', email })}
    >
      <form className="stack" onSubmit={submit} noValidate>
        {accept.isError && <Alert tone="danger">{errorMessage(accept.error)}</Alert>}
        <input type="email" autoComplete="username" value={email} readOnly hidden />
        <PasswordInput
          label={t('auth.password')}
          autoComplete="new-password"
          showRules
          email={email}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors(accept.error).password}
        />
        <PasswordInput
          label={t('auth.passwordConfirmation')}
          autoComplete="new-password"
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          error={mismatch ? t('auth.mismatch') : undefined}
        />
        <Button
          type="submit"
          variant="primary"
          size="lg"
          block
          loading={accept.isPending}
          disabled={!ok}
        >
          {t('auth.invitation.submit')}
        </Button>
      </form>
    </AuthCard>
  )
}
